import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';

const CHANNELS = { SMS: 'SMS', WHATSAPP: 'WhatsApp', EMAIL: 'E-mail' };
const STATUS = {
  QUEUED:    ['gray',  'En file'],
  SENT:      ['green', 'Envoyé'],
  SIMULATED: ['blue',  'Simulé'],
  FAILED:    ['red',   'Échec'],
};
const AUDIENCE_OPTS = [
  { value: 'unpaid',  label: 'Parents des élèves en impayé', icon: '💰' },
  { value: 'class',   label: 'Tous les parents d\'une classe', icon: '🏫' },
  { value: 'student', label: 'Les parents d\'un élève', icon: '👤' },
  { value: 'all',     label: 'Tous les parents', icon: '👨‍👩‍👧‍👦' },
  { value: 'staff',   label: 'Personnel', icon: '👨‍🏫' },
];

export default function Communication() {
  const [tab, setTab] = useState('envoyer');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'envoyer' ? 'active' : ''}`} onClick={() => setTab('envoyer')}>✉ Envoyer</button>
        <button className={`tab ${tab === 'modeles' ? 'active' : ''}`} onClick={() => setTab('modeles')}>📋 Modèles</button>
        <button className={`tab ${tab === 'historique' ? 'active' : ''}`} onClick={() => setTab('historique')}>⟲ Historique</button>
        <button className={`tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>📊 Statistiques</button>
        <button className={`tab ${tab === 'demandes' ? 'active' : ''}`} onClick={() => setTab('demandes')}>📥 Demandes parents</button>
      </div>
      {tab === 'envoyer'   && <Envoyer />}
      {tab === 'modeles'   && <Modeles />}
      {tab === 'historique'&& <Historique />}
      {tab === 'stats'     && <Stats />}
      {tab === 'demandes'  && <DemandesParents />}
    </>
  );
}

function Envoyer() {
  const [templates, setTemplates] = useState([]);
  const [classes, setClasses]     = useState([]);
  const [students, setStudents]   = useState([]);
  const [audience, setAudience]   = useState('unpaid');
  const [channel, setChannel]     = useState('SMS');
  const [templateId, setTemplateId] = useState('');
  const [body, setBody]           = useState('');
  const [classId, setClassId]     = useState('');
  const [studentId, setStudentId] = useState('');
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [sending, setSending]     = useState(false);
  const [scheduled, setScheduled] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/communication/templates'),
      api.get('/catalog/classes'),
      api.get('/students'),
    ]).then(([tpls, cls, st]) => {
      setTemplates(tpls);
      setClasses(cls);
      setStudents(st);
      if (cls[0]) setClassId(cls[0].id);
      if (st[0]) setStudentId(st[0].id);
    }).catch((e) => setError(e.message));
  }, []);

  const preview = useMemo(() => {
    const tpl = templates.find((t) => t.id === templateId);
    return body.trim() || tpl?.body || '';
  }, [body, templateId, templates]);

  const smsCount = Math.ceil((preview.length || 1) / 160);
  const charCount = preview.length;

  async function send() {
    setError(''); setResult(null); setSending(true);
    const payload = {
      channel,
      templateId: templateId || undefined,
      body: body.trim() || undefined,
      scheduledAt: scheduled || undefined,
    };
    let path = '/communication/send/unpaid';
    if (audience === 'class')   { path = '/communication/send/class';   payload.classId = classId; }
    if (audience === 'student') { path = '/communication/send/student'; payload.studentId = studentId; }
    if (audience === 'all')     { path = '/communication/send/all'; }
    if (audience === 'staff')   { path = '/communication/send/staff'; }
    try {
      const r = await api.post(path, payload);
      setResult(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="finance-grid">
      <div className="panel">
        <div className="panel-head"><h2>Composer un message</h2></div>
        <div className="panel-body" style={{ padding: 16 }}>
          {error && <div className="form-error">{error}</div>}

          <div className="fieldset-title">Destinataires</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {AUDIENCE_OPTS.map((o) => (
              <button key={o.value}
                className={audience === o.value ? 'btn' : 'btn-ghost'}
                style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.82rem', justifyContent: 'flex-start' }}
                onClick={() => setAudience(o.value)}>
                {o.icon} {o.label}
              </button>
            ))}
          </div>

          {audience === 'class' && (
            <div className="field"><label>Classe</label>
              <select value={classId} onChange={(e) => setClassId(e.target.value)}>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {audience === 'student' && (
            <div className="field"><label>Élève</label>
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
              </select>
            </div>
          )}

          <div className="fieldset-title">Canal & contenu</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field"><label>Canal</label>
              <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                {Object.entries(CHANNELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="field"><label>Modèle</label>
              <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); setBody(''); }}>
                <option value="">— Texte libre —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label>
              Message {templateId ? '(laisser vide pour utiliser le modèle)' : ''}
              <span style={{ float: 'right', color: charCount > 160 ? 'var(--danger)' : 'var(--muted)', fontSize: '0.75rem' }}>
                {charCount} car. · {smsCount} SMS
              </span>
            </label>
            <textarea rows="5" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Variables disponibles : {{eleve}} {{classe}} {{ecole}} {{solde}} {{matricule}} {{date}}" />
          </div>

          <div className="field">
            <label>Programmation (optionnel)</label>
            <input type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
            <small style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>Laisser vide pour envoi immédiat</small>
          </div>

          <button className="btn" onClick={send} disabled={sending || !preview} style={{ width: 'auto', padding: '0 24px', marginTop: 8 }}>
            {sending ? 'Envoi en cours…' : scheduled ? '📅 Programmer l\'envoi' : '✉ Envoyer maintenant'}
          </button>
        </div>
      </div>

      <div>
        {/* Aperçu */}
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-head"><h2>Aperçu du message</h2></div>
          <div className="panel-body" style={{ padding: 16 }}>
            <div style={{
              background: channel === 'WHATSAPP' ? '#e7fde7' : channel === 'EMAIL' ? '#f0f4ff' : '#f5f5f5',
              border: '1px solid var(--line)', borderRadius: 8, padding: 14,
              fontFamily: channel === 'EMAIL' ? 'Georgia, serif' : 'monospace',
              fontSize: '0.88rem', lineHeight: 1.6, minHeight: 80,
              color: preview ? 'inherit' : 'var(--muted)',
            }}>
              {preview || 'Choisis un modèle ou saisis un texte…'}
            </div>
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--muted)' }}>
              Canal : <b>{CHANNELS[channel]}</b> · {charCount} caractères · {smsCount} SMS
            </div>
          </div>
        </div>

        {/* Résultat */}
        {result && (
          <div className="panel">
            <div className="panel-head"><h2>✓ Envoi effectué</h2></div>
            <div className="panel-body" style={{ padding: 16 }}>
              <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 12 }}>
                <div className="kpi navy"><span>Total</span><strong>{result.total}</strong></div>
                <div className="kpi green"><span>Transmis</span><strong>{result.sent}</strong></div>
                <div className={`kpi ${result.failed ? 'red' : ''}`}><span>Échecs</span><strong>{result.failed}</strong></div>
              </div>
              {result.scheduled && <div className="receipt-banner">📅 Message programmé pour le {new Date(result.scheduled).toLocaleString('fr-FR')}</div>}
              <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: 8 }}>
                Mode simulation actif. Connectez une passerelle SMS/WhatsApp pour l'envoi réel.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Modeles() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: '', channel: 'SMS', body: '', category: '' });
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);

  const CATEGORIES = ['Impayés', 'Convocation', 'Résultats', 'Événement', 'Urgence', 'Autre'];

  function load() { api.get('/communication/templates').then(setTemplates).catch((e) => setError(e.message)); }
  useEffect(load, []);

  async function add(e) {
    e.preventDefault(); setError('');
    try {
      if (editId) {
        await api.patch(`/communication/templates/${editId}`, form);
        setEditId(null);
      } else {
        await api.post('/communication/templates', form);
      }
      setForm({ name: '', channel: 'SMS', body: '', category: '' });
      load();
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="finance-grid">
      <div className="panel">
        <div className="panel-head"><h2>Modèles de messages</h2></div>
        <div className="panel-body">
          {templates.length === 0 ? <div className="empty">Aucun modèle.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {templates.map((t) => (
                <div key={t.id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div>
                      <b>{t.name}</b>
                      <span className="badge blue" style={{ marginLeft: 8, fontSize: '0.7rem' }}>{CHANNELS[t.channel] || t.channel}</span>
                      {t.category && <span className="badge" style={{ marginLeft: 4, fontSize: '0.7rem' }}>{t.category}</span>}
                    </div>
                    <button className="btn-ghost btn-sm" onClick={() => { setEditId(t.id); setForm({ name: t.name, channel: t.channel, body: t.body, category: t.category || '' }); }}>Modifier</button>
                  </div>
                  <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: 0 }}>{t.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><h2>{editId ? 'Modifier le modèle' : 'Nouveau modèle'}</h2></div>
        <div className="panel-body" style={{ padding: 16 }}>
          {error && <div className="form-error">{error}</div>}
          <form onSubmit={add} className="form-grid">
            <div className="field"><label>Nom *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field"><label>Canal</label>
                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                  {Object.entries(CHANNELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="field"><label>Catégorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">—</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Texte *</label>
              <textarea rows="5" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Variables : {{eleve}} {{classe}} {{ecole}} {{solde}} {{date}}" required />
              <small style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>{form.body.length} caractères · {Math.ceil((form.body.length || 1) / 160)} SMS</small>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" type="submit" style={{ width: 'auto', padding: '0 20px' }}>{editId ? 'Enregistrer' : 'Créer'}</button>
              {editId && <button type="button" className="btn-ghost" onClick={() => { setEditId(null); setForm({ name: '', channel: 'SMS', body: '', category: '' }); }}>Annuler</button>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Historique() {
  const [rows, setRows]       = useState([]);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus]   = useState('');

  useEffect(() => {
    api.get('/communication/messages').then(setRows).catch((e) => setError(e.message));
  }, []);

  const filtered = rows.filter((m) => {
    const q = search.toLowerCase();
    const matchQ = !q || (m.recipientName || m.toAddress || '').toLowerCase().includes(q) || (m.body || '').toLowerCase().includes(q);
    const matchC = !channel || m.channel === channel;
    const matchS = !status || m.status === status;
    return matchQ && matchC && matchS;
  });

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Historique des messages</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="search-input" placeholder="Destinataire…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 180 }} />
          <select value={channel} onChange={(e) => setChannel(e.target.value)} style={{ width: 120 }}>
            <option value="">Tous canaux</option>
            {Object.entries(CHANNELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">Tous statuts</option>
            {Object.entries(STATUS).map(([v, [, l]]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {filtered.length === 0 ? <div className="empty">Aucun message.</div> : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Date</th><th>Canal</th><th>Destinataire</th><th>Message</th><th>Statut</th></tr>
            </thead>
            <tbody>{filtered.map((m) => {
              const [b, l] = STATUS[m.status] || ['gray', m.status];
              return (
                <tr key={m.id}>
                  <td className="mono">{new Date(m.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td><span className="badge blue">{CHANNELS[m.channel] || m.channel}</span></td>
                  <td>{m.recipientName || m.toAddress}<br /><small className="mono" style={{ color: 'var(--muted)' }}>{m.toAddress}</small></td>
                  <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{m.body}</td>
                  <td><span className={`badge ${b}`}>{l}</span></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function DemandesParents() {
  const [subtab, setSubtab] = useState('absences');
  const [absences, setAbsences] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [error, setError] = useState('');
  const [reponses, setReponses] = useState({});
  const [saving, setSaving] = useState({});

  function loadAbsences() {
    api.get('/registry/declarations-absences').then(setAbsences).catch((e) => setError(e.message));
  }
  function loadReclamations() {
    api.get('/registry/reclamations-parents').then(setReclamations).catch((e) => setError(e.message));
  }

  useEffect(() => { loadAbsences(); loadReclamations(); }, []);

  async function updateStatut(registry, record, statut, reload) {
    setSaving((s) => ({ ...s, [record.id]: true }));
    try {
      const reponse = reponses[record.id] || undefined;
      await api.patch(`/registry/${registry}/${record.id}`, {
        data: { ...record, statut, ...(reponse ? { reponse } : {}) },
        label: record.label || registry,
        searchText: record.searchText || '',
      });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving((s) => ({ ...s, [record.id]: false }));
    }
  }

  const BADGE = {
    'En attente': 'gray',
    'Acceptée': 'green',
    'Refusée': 'red',
    'En cours': 'blue',
    'Résolue': 'green',
  };

  function AbsenceCard({ r }) {
    return (
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <b>{r.nomEleve || r.studentName || '—'}</b>
            {r.classeNom && <span className="badge" style={{ marginLeft: 8, fontSize: '0.7rem' }}>{r.classeNom}</span>}
            <span className={`badge ${BADGE[r.statut] || 'gray'}`} style={{ marginLeft: 8, fontSize: '0.7rem' }}>{r.statut || 'En attente'}</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : ''}
          </span>
        </div>
        <div style={{ fontSize: '0.83rem', color: 'var(--muted)', marginTop: 6 }}>
          <b>Motif :</b> {r.motif || '—'} ·{' '}
          <b>Du</b> {r.dateDebut ? new Date(r.dateDebut).toLocaleDateString('fr-FR') : '—'}{' '}
          <b>au</b> {r.dateFin ? new Date(r.dateFin).toLocaleDateString('fr-FR') : '—'}
        </div>
        {r.commentaire && <div style={{ fontSize: '0.82rem', marginTop: 4 }}>{r.commentaire}</div>}
        {r.reponse && <div style={{ fontSize: '0.82rem', marginTop: 4, fontStyle: 'italic', color: 'var(--accent)' }}>Réponse : {r.reponse}</div>}
        {(!r.statut || r.statut === 'En attente') && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <textarea rows="2" placeholder="Réponse (optionnel)"
              value={reponses[r.id] || ''}
              onChange={(e) => setReponses((prev) => ({ ...prev, [r.id]: e.target.value }))}
              style={{ fontSize: '0.82rem', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" style={{ background: 'var(--success)', width: 'auto', padding: '4px 14px', fontSize: '0.82rem' }}
                disabled={saving[r.id]}
                onClick={() => updateStatut('declarations-absences', r, 'Acceptée', loadAbsences)}>
                ✓ Accepter
              </button>
              <button className="btn" style={{ background: 'var(--danger)', width: 'auto', padding: '4px 14px', fontSize: '0.82rem' }}
                disabled={saving[r.id]}
                onClick={() => updateStatut('declarations-absences', r, 'Refusée', loadAbsences)}>
                ✗ Refuser
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function ReclamationCard({ r }) {
    return (
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <b>{r.parentEmail || '—'}</b>
            {r.nomEleve && <span className="badge" style={{ marginLeft: 8, fontSize: '0.7rem' }}>{r.nomEleve}</span>}
            <span className={`badge ${BADGE[r.statut] || 'gray'}`} style={{ marginLeft: 8, fontSize: '0.7rem' }}>{r.statut || 'En attente'}</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : ''}
          </span>
        </div>
        <div style={{ fontSize: '0.83rem', color: 'var(--muted)', marginTop: 6 }}>
          <b>Motif :</b> {r.motif || '—'}
        </div>
        {r.description && <div style={{ fontSize: '0.82rem', marginTop: 4 }}>{r.description}</div>}
        {r.reponse && <div style={{ fontSize: '0.82rem', marginTop: 4, fontStyle: 'italic', color: 'var(--accent)' }}>Réponse : {r.reponse}</div>}
        {(!r.statut || r.statut === 'En attente' || r.statut === 'En cours') && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <textarea rows="2" placeholder="Réponse (optionnel)"
              value={reponses[r.id] || ''}
              onChange={(e) => setReponses((prev) => ({ ...prev, [r.id]: e.target.value }))}
              style={{ fontSize: '0.82rem', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              {r.statut !== 'En cours' && (
                <button className="btn" style={{ background: 'var(--accent)', width: 'auto', padding: '4px 14px', fontSize: '0.82rem' }}
                  disabled={saving[r.id]}
                  onClick={() => updateStatut('reclamations-parents', r, 'En cours', loadReclamations)}>
                  ↺ En cours
                </button>
              )}
              <button className="btn" style={{ background: 'var(--success)', width: 'auto', padding: '4px 14px', fontSize: '0.82rem' }}
                disabled={saving[r.id]}
                onClick={() => updateStatut('reclamations-parents', r, 'Résolue', loadReclamations)}>
                ✓ Résoudre
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const pendingAbsences = absences.filter((r) => !r.statut || r.statut === 'En attente').length;
  const pendingReclam   = reclamations.filter((r) => !r.statut || r.statut === 'En attente' || r.statut === 'En cours').length;

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Demandes des parents</h2>
      </div>
      <div className="panel-body" style={{ padding: 16 }}>
        {error && <div className="form-error">{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button className={subtab === 'absences' ? 'btn' : 'btn-ghost'} style={{ width: 'auto', padding: '6px 16px', position: 'relative' }}
            onClick={() => setSubtab('absences')}>
            Déclarations d&apos;absence
            {pendingAbsences > 0 && <span style={{ marginLeft: 6, background: 'var(--danger)', color: '#fff', borderRadius: 99, fontSize: '0.7rem', padding: '1px 7px' }}>{pendingAbsences}</span>}
          </button>
          <button className={subtab === 'reclamations' ? 'btn' : 'btn-ghost'} style={{ width: 'auto', padding: '6px 16px' }}
            onClick={() => setSubtab('reclamations')}>
            Réclamations
            {pendingReclam > 0 && <span style={{ marginLeft: 6, background: 'var(--danger)', color: '#fff', borderRadius: 99, fontSize: '0.7rem', padding: '1px 7px' }}>{pendingReclam}</span>}
          </button>
        </div>

        {subtab === 'absences' && (
          absences.length === 0
            ? <div className="empty">Aucune déclaration d&apos;absence.</div>
            : absences.map((r) => <AbsenceCard key={r.id} r={r} />)
        )}
        {subtab === 'reclamations' && (
          reclamations.length === 0
            ? <div className="empty">Aucune réclamation.</div>
            : reclamations.map((r) => <ReclamationCard key={r.id} r={r} />)
        )}
      </div>
    </div>
  );
}

function Stats() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/communication/messages').then(setRows).catch((e) => setError(e.message));
  }, []);

  const total  = rows.length;
  const sent   = rows.filter((m) => m.status === 'SENT' || m.status === 'SIMULATED').length;
  const failed = rows.filter((m) => m.status === 'FAILED').length;
  const byChannel = Object.fromEntries(
    Object.keys(CHANNELS).map((c) => [c, rows.filter((m) => m.channel === c).length])
  );

  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Total messages</span><strong>{total}</strong></div>
        <div className="kpi green"><span>Envoyés</span><strong>{sent}</strong></div>
        <div className="kpi"><span>Échecs</span><strong>{failed}</strong></div>
        <div className="kpi gold"><span>Taux succès</span><strong>{total > 0 ? Math.round(sent / total * 100) : 0}%</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head"><h2>Répartition par canal</h2></div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          <div className="table-wrap"><table>
            <thead><tr><th>Canal</th><th className="num">Messages</th><th className="num">% du total</th></tr></thead>
            <tbody>{Object.entries(CHANNELS).map(([v, l]) => (
              <tr key={v}>
                <td><span className="badge blue">{l}</span></td>
                <td className="num">{byChannel[v] || 0}</td>
                <td className="num">{total > 0 ? Math.round((byChannel[v] || 0) / total * 100) : 0}%</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
